import { Form, Formik, useField, useFormikContext } from "formik";
import { ChevronRight, Eye, FileText } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { SanitizedHtml } from "@/components/common/sanitized-html";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SignatureDialog } from "@/features/forms/components/branding-dialogs";
import { CheckboxField } from "@/features/settings/components/checkbox-field";
import { SelectField } from "@/features/settings/components/select-field";
import { useAuthedImage } from "@/hooks/use-authed-image";
import {
  ALIGN_OPTIONS,
  type FirmSettings,
  LETTER_FONT_OPTIONS,
  type LetterFont,
  type LogoAlign,
  letterheadPreview,
  updateFirmSettings,
  uploadLetterLogo,
} from "@/features/settings/firm-api";
import {
  SettingsSection,
  SettingsStickyBar,
} from "@/features/settings/components/settings-section";
import { cn } from "@/lib/utils";
import { ApiError } from "@/lib/api";

function errMessage(err: unknown, fallback: string) {
  return err instanceof ApiError ? err.message : fallback;
}

/** Formik-bound multi-line header field (raw HTML allowed when it starts with <). */
function HeaderField() {
  const [field, meta] = useField("letterHeader");
  const hasError = meta.touched && !!meta.error;
  return (
    <div className="grid gap-2">
      <Label htmlFor="letterHeader">Default header</Label>
      <Textarea id="letterHeader" rows={3} aria-invalid={hasError} {...field} />
      <p className="text-muted-foreground text-xs">
        Plain text is aligned below; start with &lt; to use raw HTML.
      </p>
      {hasError && <p className="text-destructive text-sm">{meta.error}</p>}
    </div>
  );
}

const ALIGN_ITEMS: Record<LogoAlign, string> = {
  L: "items-start text-left",
  C: "items-center text-center",
  R: "items-end text-right",
};

/**
 * Live, form-driven preview of the firm letterhead on US Letter — reflects the
 * current header text + alignment, footer, top margin, font and logo as they're
 * edited. Purely presentational (the real PDF comes from "Preview letter PDF").
 */
function LetterheadPreview({ logoSrc }: { logoSrc: string | undefined }) {
  const { values } = useFormikContext<LetterheadValues>();
  const align = ALIGN_ITEMS[values.letterHeaderAlign];
  const fontFamily =
    values.letterFont === "TIMES" ? '"Times New Roman", Georgia, serif' : "Arial, Helvetica, sans-serif";
  const header = values.letterHeader?.trim();
  const footer = values.letterFooter?.trim();

  return (
    <div
      className="relative flex flex-col gap-4 overflow-hidden rounded-md border bg-white px-8 py-7 text-[11px] text-foreground shadow-[0_1px_2px_rgba(18,26,46,0.06),0_20px_40px_-24px_rgba(18,26,46,0.35)]"
      style={{ fontFamily }}
    >
      <span aria-hidden className="bg-success absolute inset-x-0 top-0 h-[5px]" />
      <span className="text-muted-foreground absolute top-3 right-3 rounded-[4px] border border-dashed px-1.5 py-0.5 text-[9px] tabular-nums">
        {values.letterTopMargin} pt
      </span>

      {/* Header */}
      <div className={cn("flex flex-col gap-1.5", align)}>
        {values.letterUseLogo &&
          (logoSrc ? (
            <img src={logoSrc} alt="" className="h-11 max-w-[120px] object-contain" />
          ) : (
            <span className="border-success text-success flex h-11 w-[70px] items-center justify-center rounded-md border border-dashed bg-success-soft text-[9px] font-bold tracking-wide uppercase">
              Logo
            </span>
          ))}
        {header ? (
          header.startsWith("<") ? (
            <SanitizedHtml html={header} className="prose-xs" />
          ) : (
            <span className="whitespace-pre-line leading-relaxed">{header}</span>
          )
        ) : (
          <span className="text-muted-foreground whitespace-pre-line leading-relaxed">
            {"Tax FLORA LLC\n[Street address] · [City, State ZIP]\n[Phone] · [Email]"}
          </span>
        )}
      </div>

      <span className="bg-border h-px" />

      {/* Body */}
      <div className="text-muted-foreground flex flex-col gap-2 leading-relaxed">
        <span className="tabular-nums">October 13, 2026</span>
        <span>
          Internal Revenue Service
          <br />
          [Service center address]
        </span>
        <span className="text-foreground font-semibold">
          Re: Response re: {"{client_name}"} — Case {"{case_number}"}
        </span>
        <span className="flex flex-col gap-1.5">
          <span>Dear Sir or Madam,</span>
          <span className="bg-muted inline-block h-2 w-[92%] rounded-full" />
          <span className="bg-muted inline-block h-2 w-[55%] rounded-full" />
        </span>
      </div>

      {/* Signature */}
      <div className="mt-1.5 flex flex-col gap-1">
        <span className="text-muted-foreground border-b pb-0.5 text-[10px] italic">
          [Signature]
        </span>
        <span className="font-semibold">{"{practitioner_name}"}, CPA</span>
      </div>

      <span className="text-muted-foreground mt-1 border-t pt-2.5 text-center text-[9px]">
        {footer || "[Footer line]"} · Page 1 of 1
      </span>
    </div>
  );
}

const schema = Yup.object({
  letterFont: Yup.string().required(),
  letterHeader: Yup.string().max(5000, "Header is too long"),
  letterHeaderAlign: Yup.string().required(),
  letterFooter: Yup.string().max(250, "Footer must be 250 characters or fewer"),
  letterTopMargin: Yup.number()
    .typeError("Enter a number")
    .integer("Whole numbers only")
    .min(0, "Must be 0 or more")
    .max(200, "Must be 200 or less")
    .required("Required"),
  letterLogoAlign: Yup.string().required(),
  letterLogoScale: Yup.number().min(1, "1–100").max(100, "1–100").required(),
  letterLogoSpaceBelow: Yup.number().min(0, "Must be 0 or more").required(),
  logoDpi: Yup.number().min(1, "1–600").max(600, "1–600").required(),
});

interface LetterheadValues {
  letterFont: LetterFont;
  letterHeader: string;
  letterHeaderAlign: LogoAlign;
  letterFooter: string;
  letterTopMargin: number;
  letterUseLogo: boolean;
  letterLogoAlign: LogoAlign;
  letterLogoScale: number;
  letterLogoSpaceBelow: number;
  logoDpi: number;
}

interface Props {
  settings: FirmSettings;
  setSettings: (s: FirmSettings) => void;
  isAdmin: boolean;
}

/**
 * Settings › Letters — Letterhead & print layout (legacy `settings_letters.php`).
 * Firm letterhead applied to every generated letter: font, top margin, default
 * header + alignment, footer, and an optional letterhead logo (upload +
 * scale/align/DPI/spacing). A live preview on the right reflects edits; "Preview
 * letter PDF" renders a true sample with the current values.
 */
export function LetterheadSection({ settings, setSettings, isAdmin }: Props) {
  const letterLogoSrc = useAuthedImage(
    settings.letterLogoUrl
      ? `/firm/settings/media/letter-logo?v=${encodeURIComponent(settings.letterLogoUrl)}`
      : null,
  );
  const [signaturesOpen, setSignaturesOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function handleSubmit(values: LetterheadValues) {
    try {
      const updated = await updateFirmSettings({
        letterFont: values.letterFont,
        letterHeader: values.letterHeader,
        letterHeaderAlign: values.letterHeaderAlign,
        letterFooter: values.letterFooter,
        letterTopMargin: Number(values.letterTopMargin),
        letterUseLogo: values.letterUseLogo,
        letterLogoAlign: values.letterLogoAlign,
        letterLogoScale: Number(values.letterLogoScale),
        letterLogoSpaceBelow: Number(values.letterLogoSpaceBelow),
        logoDpi: Number(values.logoDpi),
      });
      setSettings(updated);
      toast.success("Letterhead saved");
    } catch (err) {
      toast.error(errMessage(err, "Couldn't save the letterhead."));
    }
  }

  async function handleLogoUpload(file: File) {
    setUploading(true);
    try {
      const updated = await uploadLetterLogo(file);
      setSettings(updated);
      toast.success("Letterhead logo uploaded");
    } catch (err) {
      toast.error(errMessage(err, "Couldn't upload the logo."));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handlePreview() {
    setPreviewing(true);
    try {
      const blob = await letterheadPreview();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(errMessage(err, "Couldn't render the preview."));
    } finally {
      setPreviewing(false);
    }
  }

  return (
    <>
      <Formik<LetterheadValues>
        initialValues={{
          letterFont: settings.letterFont,
          letterHeader: settings.letterHeader,
          letterHeaderAlign: settings.letterHeaderAlign,
          letterFooter: settings.letterFooter,
          letterTopMargin: settings.letterTopMargin,
          letterUseLogo: settings.letterUseLogo,
          letterLogoAlign: settings.letterLogoAlign,
          letterLogoScale: settings.letterLogoScale,
          letterLogoSpaceBelow: settings.letterLogoSpaceBelow,
          logoDpi: settings.logoDpi,
        }}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, dirty }) => (
          <Form className="flex flex-col gap-6">
            <SettingsSection
              title="Letterhead & print layout"
              icon={FileText}
              iconWellClassName="bg-info-soft text-info"
              subtitle="Applied to every generated letter PDF. Logo and colour come from Company › Branding."
              headerAction={
                <button
                  type="button"
                  onClick={() => setSignaturesOpen(true)}
                  className="text-info inline-flex items-center gap-1.5 text-[13px] font-semibold"
                >
                  Manage signatures <ChevronRight className="size-3.5" />
                </button>
              }
              bodyClassName="grid grid-cols-1 gap-0 p-0 md:grid-cols-2"
            >
              {/* Controls */}
              <div className="flex flex-col gap-6 border-b p-6 md:border-r md:border-b-0">
                <fieldset disabled={!isAdmin} className="grid gap-6">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <SelectField name="letterFont" label="Font" options={LETTER_FONT_OPTIONS} />
                    <FormField
                      name="letterTopMargin"
                      label="Top margin (pt)"
                      type="number"
                      min={0}
                      max={200}
                    />
                  </div>

                  <HeaderField />
                  <div className="grid gap-6 sm:grid-cols-2">
                    <SelectField
                      name="letterHeaderAlign"
                      label="Header alignment"
                      options={ALIGN_OPTIONS}
                    />
                    <FormField
                      name="letterFooter"
                      label="Letter footer (centered, up to 2 lines)"
                      maxLength={250}
                    />
                  </div>

                  {/* Logo */}
                  <div className="grid gap-3 rounded-lg border p-4">
                    <CheckboxField
                      name="letterUseLogo"
                      label="Show a logo at the top of letters"
                    />
                    <div className="flex items-center gap-3">
                      <div className="bg-muted flex h-16 w-32 items-center justify-center overflow-hidden rounded border">
                        {letterLogoSrc ? (
                          <img
                            src={letterLogoSrc}
                            alt="Letterhead logo"
                            className="max-h-full max-w-full object-contain"
                          />
                        ) : (
                          <span className="text-muted-foreground text-xs">
                            {settings.letterLogoUrl ? "Loading…" : "No logo"}
                          </span>
                        )}
                      </div>
                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleLogoUpload(file);
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={!isAdmin || uploading}
                        onClick={() => fileRef.current?.click()}
                      >
                        {uploading
                          ? "Uploading…"
                          : settings.letterLogoUrl
                            ? "Replace logo"
                            : "Upload logo"}
                      </Button>
                    </div>
                    <div className="grid gap-6 sm:grid-cols-2">
                      <SelectField
                        name="letterLogoAlign"
                        label="Logo alignment"
                        options={ALIGN_OPTIONS}
                      />
                      <FormField
                        name="letterLogoScale"
                        label="Logo scale (%)"
                        type="number"
                        min={1}
                        max={100}
                      />
                      <FormField
                        name="letterLogoSpaceBelow"
                        label="Space below (pt)"
                        type="number"
                        min={0}
                      />
                      <FormField name="logoDpi" label="Logo DPI" type="number" min={1} max={600} />
                    </div>
                  </div>
                </fieldset>
                {!isAdmin && (
                  <p className="text-muted-foreground text-sm">
                    Only a country director can edit the letterhead.
                  </p>
                )}
              </div>

              {/* Live preview */}
              <div className="bg-muted/40 flex flex-col gap-3.5 p-6">
                <span className="text-muted-foreground inline-flex items-center gap-2 text-xs font-semibold tracking-[0.08em] uppercase">
                  <Eye className="size-3.5" /> Live preview · US Letter
                </span>
                <LetterheadPreview logoSrc={letterLogoSrc} />
              </div>
            </SettingsSection>

            <SettingsStickyBar>
              <Button
                type="button"
                variant="outline"
                onClick={handlePreview}
                disabled={previewing}
              >
                <Eye /> {previewing ? "Rendering…" : "Preview letter PDF"}
              </Button>
              {isAdmin && (
                <Button type="submit" disabled={isSubmitting || !dirty}>
                  {isSubmitting ? "Saving…" : "Save letterhead"}
                </Button>
              )}
            </SettingsStickyBar>
          </Form>
        )}
      </Formik>

      <SignatureDialog open={signaturesOpen} onOpenChange={setSignaturesOpen} />
    </>
  );
}
